import {
  BankOutlined,
  DashboardOutlined,
  FileTextOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import React from 'react';
import type { TFunction } from 'i18next';
import { PERMISSIONS } from './permissions';

export type MenuItem = Required<MenuProps>['items'][number];

export interface MenuItemWithPermission {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  permission?: string;
  children?: MenuItemWithPermission[];
}

/**
 * Filter menu items based on user permissions
 * @param items - Menu items with permission field
 * @param hasPermission - Function to check if user has permission
 * @returns Filtered menu items
 */
export const filterMenuByPermissions = (
  items: MenuItemWithPermission[],
  hasPermission: (permission: string) => boolean
): MenuItem[] => {
  const result: MenuItem[] = [];

  for (const item of items) {
    // If no permission specified, show the item
    if (item.permission && !hasPermission(item.permission)) {
      continue;
    }

    // If item has children, recursively filter them
    if (item.children) {
      const filteredChildren = filterMenuByPermissions(item.children, hasPermission);

      // If no children remain after filtering, don't show parent
      if (filteredChildren.length === 0) continue;

      result.push({
        key: item.key,
        label: item.label,
        icon: item.icon,
        children: filteredChildren,
      });
    } else {
      result.push({
        key: item.key,
        label: item.label,
        icon: item.icon,
      });
    }
  }

  return result;
};

/**
 * Get menu items with translations
 * @param t - Translation function from useTranslation hook
 * @returns Array of menu items with translated labels
 */
export const getMenuItems = (t: TFunction): MenuItemWithPermission[] => [
  {
    key: '/',
    icon: React.createElement(DashboardOutlined),
    label: t('menu.home'),
  },
  {
    key: '/order',
    icon: React.createElement(ShoppingCartOutlined),
    label: t('menu.order'),
    permission: PERMISSIONS.ORDER.INDEX,
  },
  {
    key: '/company',
    icon: React.createElement(BankOutlined),
    label: t('menu.company'),
    permission: PERMISSIONS.COMPANY.INDEX,
  },
  {
    key: '/partner',
    icon: React.createElement(TeamOutlined),
    label: t('menu.partner'),
    permission: PERMISSIONS.PARTNER.INDEX,
  },
  {
    key: '/application',
    icon: React.createElement(FileTextOutlined),
    label: t('menu.application'),
    permission: PERMISSIONS.APPLICATION.INDEX,
  },
  {
    key: 'user-settings',
    icon: React.createElement(UserOutlined),
    label: t('menu.userSettings'),
    children: [
      { key: '/agent', label: t('menu.agent'), permission: PERMISSIONS.AGENT.INDEX },
      { key: '/courier', label: t('menu.courier'), permission: PERMISSIONS.COURIER.INDEX },
      { key: '/courier-task', label: t('menu.courierTask'), permission: PERMISSIONS.COURIER_TASK.INDEX },
      { key: '/kassa', label: t('menu.kassa'), permission: PERMISSIONS.KASSA.INDEX },
      { key: '/user', label: t('menu.user'), permission: PERMISSIONS.USER.INDEX },
      { key: '/staff', label: t('menu.staff'), permission: PERMISSIONS.STAFF.INDEX },
      { key: '/role', label: t('menu.role'), permission: PERMISSIONS.ROLE.INDEX },
      { key: '/task', label: t('menu.task'), permission: PERMISSIONS.TASK.INDEX },
    ],
  },
  {
    key: 'settings',
    icon: React.createElement(SettingOutlined),
    label: t('menu.settings'),
    children: [
      { key: '/product', label: t('menu.product'), permission: PERMISSIONS.PRODUCT.INDEX },
      { key: '/brand', label: t('menu.brand'), permission: PERMISSIONS.BRAND.INDEX },
      { key: '/category', label: t('menu.category'), permission: PERMISSIONS.CATEGORY.INDEX },
      { key: '/pack', label: t('menu.pack'), permission: PERMISSIONS.PACK.INDEX },
      { key: '/warehouse', label: t('menu.warehouse'), permission: PERMISSIONS.WAREHOUSE.INDEX },
      { key: '/purchase', label: t('menu.purchase'), permission: PERMISSIONS.PURCHASE.INDEX },
      { key: '/geo-zone', label: t('menu.geoZone'), permission: PERMISSIONS.GEO_ZONE.INDEX },
      { key: '/agent-geo-zone', label: t('menu.agentGeoZone'), permission: PERMISSIONS.AGENT_GEO_ZONE.INDEX },
    ],
  },
];

// Keep menuItems for backward compatibility (will be deprecated)
// Use getMenuItems instead
export const menuItems: MenuItem[] = [
  {
    key: '/',
    icon: React.createElement(DashboardOutlined),
    label: 'Home',
  },
  {
    key: '/order',
    icon: React.createElement(ShoppingCartOutlined),
    label: 'Orders',
  },
  {
    key: '/partner',
    icon: React.createElement(TeamOutlined),
    label: 'Partners',
  },
  {
    key: '/application',
    icon: React.createElement(FileTextOutlined),
    label: 'Applications',
  },
  {
    key: 'user-settings',
    icon: React.createElement(UserOutlined),
    label: 'User Settings',
    children: [
      { key: '/agent', label: 'Agent' },
      { key: '/courier', label: 'Courier' },
      { key: '/courier-task', label: 'Courier task' },
      { key: '/kassa', label: 'Kassa' },
      { key: '/user', label: 'User' },
      { key: '/staff', label: 'Staff' },
      { key: '/role', label: 'Role' },
      { key: '/task', label: 'Task' },
    ],
  },
  {
    key: 'settings',
    icon: React.createElement(SettingOutlined),
    label: 'Settings',
    children: [
      { key: '/product', label: 'Product' },
      { key: '/brand', label: 'Brand' },
      { key: '/category', label: 'Category' },
      { key: '/pack', label: 'Pack' },
      { key: '/warehouse', label: 'Warehouse' },
      { key: '/purchase', label: 'Purchase' },
      { key: '/geo-zone', label: 'Geo-zone' },
      { key: '/agent-geo-zone', label: 'Agent geo-zone' },
    ],
  },
];

type AnyMenuItem = MenuItem | MenuItemWithPermission;

/**
 * Type guard to check if menu item has children
 */
const hasChildren = <T extends AnyMenuItem>(item: T): item is T & { children: AnyMenuItem[] } => {
  return item !== null && typeof item === 'object' && 'children' in item && Array.isArray(item.children);
};

/**
 * Get menu key from pathname
 * Supports nested routes (e.g., /brand/create -> /brand)
 * @param pathname - Current location pathname
 * @returns Menu key or null if not found
 */
export const getMenuKeyFromPath = (pathname: string, t?: TFunction): string | null => {
  const items: AnyMenuItem[] = t ? getMenuItems(t) : menuItems;

  // Check exact match first
  for (const item of items) {
    if (item && typeof item === 'object' && 'key' in item && item.key === pathname) {
      return item.key as string;
    }
  }

  // Check children for exact match
  for (const item of items) {
    if (item && hasChildren(item)) {
      const childMatch = item.children.find((child) => child && typeof child === 'object' && 'key' in child && child.key === pathname);
      if (childMatch && typeof childMatch === 'object' && 'key' in childMatch) return childMatch.key as string;
    }
  }

  // Check for nested routes (e.g., /brand/create -> /brand)
  // Find the longest matching path
  let bestMatch: string | null = null;
  let bestMatchLength = 0;

  // Check root menu items
  for (const item of items) {
    if (item && typeof item === 'object' && 'key' in item && item.key && typeof item.key === 'string') {
      const menuPath = item.key;
      if (pathname.startsWith(menuPath) && menuPath.length > bestMatchLength) {
        // Make sure it's a complete segment match (not partial)
        const nextChar = pathname[menuPath.length];
        if (!nextChar || nextChar === '/') {
          bestMatch = menuPath;
          bestMatchLength = menuPath.length;
        }
      }
    }
  }

  // Check children for nested routes
  for (const item of items) {
    if (item && hasChildren(item)) {
      for (const child of item.children) {
        if (child && typeof child === 'object' && 'key' in child && child.key && typeof child.key === 'string') {
          const menuPath = child.key;
          if (pathname.startsWith(menuPath) && menuPath.length > bestMatchLength) {
            // Make sure it's a complete segment match (not partial)
            const nextChar = pathname[menuPath.length];
            if (!nextChar || nextChar === '/') {
              bestMatch = menuPath;
              bestMatchLength = menuPath.length;
            }
          }
        }
      }
    }
  }

  // Return best match or default to home if path is root
  return bestMatch || (pathname === '/' ? '/' : null);
};

/**
 * Get parent menu key if the given key is a child
 * @param key - Menu key
 * @returns Parent menu key or null
 */
export const getParentMenuKey = (key: string, t?: TFunction): string | null => {
  const items: AnyMenuItem[] = t ? getMenuItems(t) : menuItems;

  for (const item of items) {
    if (item && hasChildren(item)) {
      const childMatch = item.children.find((child) => child && typeof child === 'object' && 'key' in child && child.key === key);
      if (childMatch && typeof item === 'object' && 'key' in item) return item.key as string;
    }
  }
  return null;
};
